"use client";

// The actual product render, shared by the admin calibration tool (components/admin/calibration-tool.tsx)
// and the customer-facing /customize live preview (components/customize/customize-preview-gallery.tsx) --
// one real implementation instead of two copies drifting apart. History of how each piece here was
// actually arrived at (self-correcting orientation, the metalness/material bug, the CSS box-crop bug,
// the depth/zoom-vs-position bug) lives in the checklist's Decisions Log against the original
// /dev/color-poc proof-of-concept -- none of it is re-derived here, just relocated.

import { useEffect, useMemo, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { Environment, useGLTF } from "@react-three/drei";
import * as THREE from "three";

// Default/fallback only now — which model actually loads is admin-configurable (StoreSettings'
// ThreeD:ActiveModelFile, see CommerceSettingsController), threaded down as Model's modelPath prop
// by every real caller. Kept as a named export (still used for the static preload below, and by the
// /dev proof-of-concept pages, which don't go through the admin setting at all) rather than removed.
export const GLB_PATH = "/models/basaltmount-poc.glb";
export const HDRI_PATH = "/hdri/kloofendal_overcast_puresky_1k.hdr";

// Admin-provided values are bare filenames (e.g. "basaltmount-cap.glb"), matching how the setting is
// described on the admin Settings page — this turns one into the actual /models/ URL useGLTF needs.
// Accepts an already-rooted path unchanged (startsWith("/")), so passing GLB_PATH itself still works.
export function modelUrlForFile(fileName: string): string {
  return fileName.startsWith("/") ? fileName : `/models/${fileName}`;
}

export const GROUPS = [
  { key: "A", label: "Cap", meshName: "Cap" },
  { key: "B", label: "Body", meshName: "Main_Body" },
  { key: "C", label: "Bottom", meshName: "Locker" },
  { key: "D", label: "Screw Cap", meshName: "screw_cap" },
] as const;
export type GroupKey = (typeof GROUPS)[number]["key"];

export function groupForMeshName(name: string): GroupKey | undefined {
  return GROUPS.find((g) => name === g.meshName || name.startsWith(`${g.meshName}_`))?.key;
}

type MeshEntry = { uuid: string; name: string; mesh: THREE.Mesh };

function worldCenterY(mesh: THREE.Mesh): number {
  return new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3()).y;
}

// Injected into each part's MeshStandardMaterial via onBeforeCompile -- computes each fragment's real
// world position (post placement transform, so it lines up with the admin tool's DepthFadeGizmo's own
// world-space pivot), projects it onto the fade axis relative to the pivot, and fades diffuseColor.a from
// 1 to 0 across fadeStart..fadeEnd. Injected once at material-clone time; `#include <opaque_fragment>` is
// the chunk that turns diffuseColor.a into gl_FragColor.a, and forces alpha back to 1 when the material
// isn't transparent -- material.transparent must stay true for this to have any visible effect. Uniform
// VALUES (not the shader itself) are kept live via the separate effect below, so dragging a slider updates
// the fade without paying for a shader recompile on every frame. Ported from the /dev/color-poc proof of
// concept once the axis/zone definition was confirmed there against a real photo.
function injectDepthFadeShader(material: THREE.MeshStandardMaterial, shader: THREE.WebGLProgramParametersWithUniforms) {
  shader.uniforms.fadeAxis = { value: new THREE.Vector3(0, 1, 0) };
  shader.uniforms.fadePivot = { value: new THREE.Vector3(0, 0, 0) };
  shader.uniforms.fadeStart = { value: 1e6 };
  shader.uniforms.fadeEnd = { value: 1e6 + 1 };
  material.userData.fadeShader = shader;

  shader.vertexShader = shader.vertexShader
    .replace("#include <common>", "#include <common>\nvarying vec3 vFadeWorldPos;")
    .replace("#include <begin_vertex>", "#include <begin_vertex>\nvFadeWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;");
  shader.fragmentShader = shader.fragmentShader
    .replace(
      "#include <common>",
      "#include <common>\nuniform vec3 fadeAxis;\nuniform vec3 fadePivot;\nuniform float fadeStart;\nuniform float fadeEnd;\nvarying vec3 vFadeWorldPos;",
    )
    .replace(
      "#include <opaque_fragment>",
      "float fadeDist = dot(vFadeWorldPos - fadePivot, fadeAxis);\ndiffuseColor.a *= 1.0 - smoothstep(fadeStart, fadeEnd, fadeDist);\n#include <opaque_fragment>",
    );
}

// Callers with no calibrated placement/photo context (there are none today, but this keeps Model usable
// without a fade config) get fadeStart/fadeEnd far enough apart that smoothstep never actually transitions
// -- genuinely no visible fade, rather than defaulting to DEFAULT_DEPTH_FADE below (which fades real
// geometry starting right at the pivot -- a sensible calibration starting point, not a "no-op" one).
const NO_DEPTH_FADE = { fadeAzimuthDeg: 0, fadeElevationDeg: 0, fadeStart: 1e6, fadeEnd: 1e6 + 1 };

export function Model({
  groupColors, depthFade = NO_DEPTH_FADE, pivot = [0, 0, 0], onColorsApplied, modelPath = GLB_PATH,
}: {
  groupColors: Record<GroupKey, string>;
  depthFade?: DepthFadeConfig;
  pivot?: readonly [number, number, number];
  // Fired at the end of the per-part colour effect below -- the model has stopped rendering in the
  // GLB's default grey. /customize uses this to know when a preview tile is safe to reveal.
  onColorsApplied?: () => void;
  // Full /models/... URL (see modelUrlForFile) -- defaults to GLB_PATH for callers with no admin
  // setting to read from (there are none in the real app today, but keeps Model usable standalone).
  modelPath?: string;
}) {
  const { scene } = useGLTF(modelPath);
  // Read by injectDepthFadeShader's closure at whatever moment the material actually (re)compiles --
  // could be this render or several renders later (WebGL compiles lazily, on first real draw) -- so the
  // ref, not the prop directly, since `cloned` below is only computed once per model load and can't
  // re-run just because depthFade/pivot changed on some later render.
  const depthFadeRef = useRef(depthFade);
  const pivotRef = useRef(pivot);
  depthFadeRef.current = depthFade;
  pivotRef.current = pivot;

  const cloned = useMemo(() => {
    const root = scene.clone(true);
    root.updateMatrixWorld(true);
    const entries: MeshEntry[] = [];
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        const src = mesh.material as THREE.MeshStandardMaterial;
        const mat = src.clone();
        // Source material comes through metalness:1 (a leftover CAD material) -- with no env map that
        // rendered near-black; these are matte 3D-printed plastic parts, not metal.
        mat.metalness = 0;
        mat.roughness = 0.55;
        // Depth-fade needs real alpha blending -- see injectDepthFadeShader's own comment for why this
        // has to be true for the effect to be visible at all (OPAQUE forces alpha back to 1 otherwise).
        mat.transparent = true;
        mat.onBeforeCompile = (shader) => {
          injectDepthFadeShader(mat, shader);
          const axis = depthFadeAxis(depthFadeRef.current.fadeAzimuthDeg, depthFadeRef.current.fadeElevationDeg);
          shader.uniforms.fadeAxis.value.set(...axis);
          shader.uniforms.fadePivot.value.set(...pivotRef.current);
          shader.uniforms.fadeStart.value = depthFadeRef.current.fadeStart;
          shader.uniforms.fadeEnd.value = depthFadeRef.current.fadeEnd;
        };
        mesh.material = mat;
        // Lets parts occlude each other (e.g. Cap casting a shadow onto Body) whenever a KeyLight below
        // has shadows on -- receiveShadow costs nothing to leave on even when it doesn't.
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        entries.push({ uuid: mesh.uuid, name: mesh.name, mesh });
      }
    });

    // Self-correcting "cap up" orientation -- measures Cap's and Locker's actual world-space Y position
    // as loaded and only flips 180° about X if Cap isn't already the higher of the two, so it corrects
    // itself regardless of which way any given re-export's raw axes happen to land.
    const capEntry = entries.find((e) => e.name === "Cap");
    const lockerEntry = entries.find((e) => e.name === "Locker");
    if (capEntry && lockerEntry && worldCenterY(capEntry.mesh) < worldCenterY(lockerEntry.mesh)) {
      root.rotation.x = Math.PI;
      root.updateMatrixWorld(true);
    }

    return { root, entries };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depthFade/pivot flow through via the refs above and the live-update effect below, not as a re-clone trigger
  }, [scene]);

  useEffect(() => {
    for (const { name, mesh } of cloned.entries) {
      const group = groupForMeshName(name);
      if (!group) continue;
      (mesh.material as THREE.MeshStandardMaterial).color.set(groupColors[group]);
    }
    onColorsApplied?.();
  }, [cloned, groupColors, onColorsApplied]);

  // Keeps the fade uniforms live as sliders move, WITHOUT recompiling the shader (mat.needsUpdate is
  // deliberately not touched here) -- injectDepthFadeShader's userData.fadeShader reference is how this
  // reaches the actual compiled program. Harmless no-op until the material has actually compiled at least
  // once (WebGL compiles lazily, on first real draw), since injectDepthFadeShader seeds the correct values
  // itself at compile time via the refs above.
  useEffect(() => {
    const axis = depthFadeAxis(depthFade.fadeAzimuthDeg, depthFade.fadeElevationDeg);
    for (const { mesh } of cloned.entries) {
      const shader = (mesh.material as THREE.MeshStandardMaterial).userData.fadeShader as
        | { uniforms: { fadeAxis: { value: THREE.Vector3 }; fadePivot: { value: THREE.Vector3 }; fadeStart: { value: number }; fadeEnd: { value: number } } }
        | undefined;
      if (!shader) continue;
      shader.uniforms.fadeAxis.value.set(...axis);
      shader.uniforms.fadePivot.value.set(...pivot);
      shader.uniforms.fadeStart.value = depthFade.fadeStart;
      shader.uniforms.fadeEnd.value = depthFade.fadeEnd;
    }
  }, [cloned, depthFade, pivot]);

  return <primitive object={cloned.root} />;
}

export function SceneLighting({ exposure, envIntensity, fillIntensity }: { exposure: number; envIntensity: number; fillIntensity: number }) {
  const { gl, scene } = useThree();
  useEffect(() => {
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = exposure;
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;
  }, [gl, exposure]);
  useEffect(() => {
    scene.environmentIntensity = envIntensity;
  }, [scene, envIntensity]);
  return (
    <>
      <Environment files={HDRI_PATH} background={false} />
      <directionalLight position={[30, 60, 40]} intensity={fillIntensity} />
    </>
  );
}

// A single tunable "key light" per calibration photo, on top of the fixed HDRI/fill light above -- real
// outdoor light has a dominant directional source, and matching that per-photo (not just ambient fill) is
// what makes a live render's shading actually match a given reference instead of looking flat. Saved
// alongside Placement on CalibrationImage (see AdminCalibrationImagesController / DapperCalibrationImage-
// Repository), edited via the admin calibration tool's Key Light panel, applied here for both the admin
// tool's own preview and the customer-facing /customize gallery. Field names mirror the DB columns
// (LightType -> lightType, etc., following ASP.NET's default camelCase JSON policy the same way
// Placement's posX already mirrors PosX).
export type KeyLightConfig = {
  lightType: "directional" | "spot" | "point";
  lightX: number; lightY: number; lightZ: number;
  lightIntensity: number; lightColor: string;
  lightBeamDeg: number; lightPenumbra: number; lightDecay: number;
  lightCastShadow: boolean;
};

// Matches the DF_CalibrationImages_Light* column defaults in database/Tables/CalibrationImages.sql --
// dialed in against the first real reference photo (2026-08-26, re-dialed 2026-08-27 using the
// azimuth/elevation/distance controls -- a point light, not the original spot). Single source of truth:
// the SQL defaults mirror this, not the other way around.
export const DEFAULT_KEY_LIGHT: KeyLightConfig = {
  lightType: "point",
  lightX: -23.6, lightY: -59.91, lightZ: 318.77,
  lightIntensity: 1.75, lightColor: "#ffffff",
  lightBeamDeg: 74, lightPenumbra: 0.6, lightDecay: 4,
  lightCastShadow: true,
};

const KEY_LIGHT_SHADOW_MAP_SIZE: [number, number] = [1024, 1024];

// Converts between the light's stored/saved position (lightX/Y/Z -- raw Cartesian world-space
// coordinates, unchanged, still what's actually rendered and persisted to CalibrationImage) and a
// friendlier orbit-around-the-subject control: Azimuth (0-360°, sweeps the light all the way around the
// TARGET -- 0° = directly in front, toward the camera, 90° = right, 180° = directly behind, 270° = left),
// Elevation (-90 to 90°, above/below the target's own height), and Distance from the target. Dragging raw
// X alone only slides the light sideways at whatever depth Z currently is -- it can never bring the light
// around to the front on its own, since that's a Z change, not an X change. Azimuth alone does exactly
// that. Purely a UI-layer convenience: the conversion is relative to `target` (the calibrated placement's
// own position, not world origin) each time, nothing else about how the light is stored or rendered
// changes.
export function cartesianToSpherical(
  light: readonly [number, number, number], target: readonly [number, number, number],
): { azimuthDeg: number; elevationDeg: number; distance: number } {
  const dx = light[0] - target[0];
  const dy = light[1] - target[1];
  const dz = light[2] - target[2];
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const azimuthDeg = distance > 0 ? (((Math.atan2(dx, dz) * 180) / Math.PI) + 360) % 360 : 0;
  const elevationDeg = distance > 0 ? (Math.asin(dy / distance) * 180) / Math.PI : 0;
  return { azimuthDeg, elevationDeg, distance };
}

export function sphericalToCartesian(
  azimuthDeg: number, elevationDeg: number, distance: number, target: readonly [number, number, number],
): [number, number, number] {
  const azimuthRad = (azimuthDeg * Math.PI) / 180;
  const elevationRad = (elevationDeg * Math.PI) / 180;
  const horizontalRadius = distance * Math.cos(elevationRad);
  const dx = horizontalRadius * Math.sin(azimuthRad);
  const dy = distance * Math.sin(elevationRad);
  const dz = horizontalRadius * Math.cos(azimuthRad);
  return [target[0] + dx, target[1] + dy, target[2] + dz];
}

export function KeyLight({ config, targetPosition }: { config: KeyLightConfig; targetPosition: readonly [number, number, number] }) {
  // Only spot/directional lights have a `.target` to aim -- point lights radiate in all directions.
  const ref = useRef<THREE.DirectionalLight | THREE.SpotLight>(null);
  useEffect(() => {
    const light = ref.current;
    if (!light) return;
    light.target.position.set(...targetPosition);
    light.target.updateMatrixWorld();
  }, [targetPosition, config.lightType]);

  // DirectionalLight models sunlight (infinitely far away) -- its intensity is irradiance, constant
  // regardless of distance. Spot/Point are real point sources with physically-correct
  // inverse-distance^decay falloff (three.js's photometric light model, since r150): at the ~150-300 unit
  // distances this rig uses, that falloff eats almost all of a raw "0.3" before it reaches the product.
  // Multiplying by distance^decay compensates for that, so "lightIntensity" means the same thing
  // (brightness AT the product) for every light type, while the falloff SHAPE around that point stays
  // physically real.
  const distance = Math.hypot(config.lightX - targetPosition[0], config.lightY - targetPosition[1], config.lightZ - targetPosition[2]);
  const physicalIntensity = config.lightType === "directional"
    ? config.lightIntensity
    : config.lightIntensity * Math.pow(Math.max(distance, 1), config.lightDecay);

  if (config.lightType === "point") {
    return (
      <pointLight
        position={[config.lightX, config.lightY, config.lightZ]}
        intensity={physicalIntensity}
        color={config.lightColor}
        decay={config.lightDecay}
        castShadow={config.lightCastShadow}
        shadow-mapSize={KEY_LIGHT_SHADOW_MAP_SIZE}
        shadow-camera-near={1}
        shadow-camera-far={1000}
      />
    );
  }
  if (config.lightType === "spot") {
    return (
      <spotLight
        ref={ref as React.RefObject<THREE.SpotLight>}
        position={[config.lightX, config.lightY, config.lightZ]}
        intensity={physicalIntensity}
        color={config.lightColor}
        angle={(config.lightBeamDeg * Math.PI) / 180}
        penumbra={config.lightPenumbra}
        distance={0}
        decay={config.lightDecay}
        castShadow={config.lightCastShadow}
        shadow-mapSize={KEY_LIGHT_SHADOW_MAP_SIZE}
        shadow-camera-near={1}
        shadow-camera-far={1000}
      />
    );
  }
  return (
    <directionalLight
      ref={ref as React.RefObject<THREE.DirectionalLight>}
      position={[config.lightX, config.lightY, config.lightZ]}
      intensity={physicalIntensity}
      color={config.lightColor}
      castShadow={config.lightCastShadow}
      shadow-mapSize={KEY_LIGHT_SHADOW_MAP_SIZE}
      shadow-camera-near={1}
      shadow-camera-far={1000}
      shadow-camera-left={-200}
      shadow-camera-right={200}
      shadow-camera-top={200}
      shadow-camera-bottom={-200}
    />
  );
}

export type Placement = { posX: number; posY: number; posZ: number; rotXDeg: number; rotYDeg: number; rotZDeg: number; scale: number };

export function applyPlacement(local: readonly [number, number, number], p: Placement): THREE.Vector3 {
  const v = new THREE.Vector3(local[0], local[1], local[2]);
  v.multiplyScalar(p.scale);
  v.applyEuler(new THREE.Euler((p.rotXDeg * Math.PI) / 180, (p.rotYDeg * Math.PI) / 180, (p.rotZDeg * Math.PI) / 180, "XYZ"));
  v.add(new THREE.Vector3(p.posX, p.posY, p.posZ));
  return v;
}

export function projectLocalPoint(local: readonly [number, number, number], p: Placement, camera: THREE.PerspectiveCamera): { x: number; y: number } {
  const world = applyPlacement(local, p);
  world.project(camera);
  return { x: world.x, y: world.y };
}

// A per-photo "depth fade" axis -- not every reference photo has its subject lying flat in the image
// plane, or leaning only in the single up/down-vs-depth plane a first version of this assumed. A part
// that should fade first for being "behind the pole" can just as easily be off to one side as above/below
// (confirmed against a real photo where a >90° tilt was needed in that one vertical plane alone -- the
// natural next question was "what about sideways?"). So this is a full spherical direction, exactly the
// same azimuth/elevation convention KeyLight already uses above (0°/0° = toward the camera, azimuth
// sweeps all the way around the vertical axis, elevation swings from straight down to straight up) --
// fadeAzimuthDeg/fadeElevationDeg together can point this axis anywhere on the unit sphere, not just
// within one plane. fadeStart/fadeEnd are distances along that axis from a pivot (the calibrated
// Placement's own position) where the fade zone begins/ends. This is currently just the axis+zone
// *definition*, visualized by calibration-tool.tsx's DepthFadeGizmo so an admin can confirm it against a
// photo before it's wired up as an actual per-vertex opacity fade on the model -- local admin state only
// for now, not yet persisted to CalibrationImage.
export type DepthFadeConfig = { fadeAzimuthDeg: number; fadeElevationDeg: number; fadeStart: number; fadeEnd: number };
export const DEFAULT_DEPTH_FADE: DepthFadeConfig = { fadeAzimuthDeg: 180, fadeElevationDeg: 90, fadeStart: 0, fadeEnd: 200 };

// A pure unit direction -- reuses sphericalToCartesian's exact azimuth/elevation math (distance=1, target
// the origin) rather than a second hand-written formula, so "azimuth/elevation mean the same thing" for
// both the Key Light's position and this axis's direction.
export function depthFadeAxis(fadeAzimuthDeg: number, fadeElevationDeg: number): [number, number, number] {
  return sphericalToCartesian(fadeAzimuthDeg, fadeElevationDeg, 1, [0, 0, 0]);
}

// preserveDrawingBuffer -- without it, a WebGL canvas's buffer is free to be cleared by the browser
// right after each frame is composited to the screen, which makes drawImage(canvas, ...)/toDataURL()
// unreliable (whether it captures the last real frame or a blank one depends on exact timing). Needed for
// the /customize lightbox's "download the blended photo+model" feature (customize-preview-gallery.tsx),
// which screenshots this canvas well after the frame that drew it. Small, constant memory/perf cost
// (retains the buffer instead of discarding it) -- negligible at this app's scale (a handful of small
// canvases, not a AAA game).
export const TRANSPARENT_CANVAS_GL = { alpha: true, antialias: true, preserveDrawingBuffer: true };
export const PLACEMENT_CAMERA = { fov: 30, position: [0, 0, 900] as const };
// Saved per photo alongside KeyLightConfig, same reasoning: the right amount of ambient HDRI/fill light
// is a real judgment call against a specific reference photo, not something derivable from the model's
// geometry. Matches the DF_CalibrationImages_{Exposure,EnvIntensity,FillIntensity} column defaults in
// database/Tables/CalibrationImages.sql -- dialed in against the first real reference photo (2026-08-26).
export type AmbientLightConfig = { exposure: number; envIntensity: number; fillIntensity: number };
export const DEFAULT_LIGHTING: AmbientLightConfig = { exposure: 0.9, envIntensity: 0.85, fillIntensity: 0.25 };

// How the /customize collage tile crops this photo's own real aspect ratio down to the gallery's uniform
// square -- admin-set and saved per photo, NOT auto-derived from the calibrated model position. An earlier
// version tried projecting the model's placement through the calibration camera to auto-pick a crop focal
// point, which broke on real photos: a placement whose projection lands near or past the camera's own
// frustum edge (easily possible -- it only has to be right for the FULL photo, not for a square window
// into it) produced a focal fraction outside [0,1], which drew the whole composite completely outside the
// destination canvas -- confirmed directly, several real collage tiles came back blank. Letting an admin
// place the crop by eye (see calibration-tool.tsx's collage-crop panel) sidesteps that whole failure mode
// entirely, and focalCoverFit below clamps defensively regardless.
export type CollageCropConfig = { collageFocalX: number; collageFocalY: number; collageZoom: number };
export const DEFAULT_COLLAGE_CROP: CollageCropConfig = { collageFocalX: 0.5, collageFocalY: 0.5, collageZoom: 1 };

// Cover-fits a sourceAspect-shaped image into a destW x destH box, anchored at (focalX, focalY) -- the
// point of the SOURCE at that fraction stays fixed at the same fraction of the DEST box, exactly like CSS
// object-position, generalized to also support zooming in beyond the minimum cover scale (zoom=1) around
// that same fixed point (like a CSS transform-origin'd scale). Both the resulting offset AND focalX/focalY
// themselves are clamped -- focalX/focalY should already be in [0,1] from the admin UI, but clamping here
// too means a bad/out-of-range value can only ever produce a full, gapless crop, never a blank canvas (see
// this type's own comment for exactly the failure mode that guards against).
export function focalCoverFit(
  sourceAspect: number, destW: number, destH: number, focalX: number, focalY: number, zoom: number,
): { drawW: number; drawH: number; offsetX: number; offsetY: number } {
  const destAspect = destW / destH;
  const baseW = sourceAspect > destAspect ? destH * sourceAspect : destW;
  const baseH = sourceAspect > destAspect ? destH : destW / sourceAspect;
  const drawW = baseW * Math.max(1, zoom);
  const drawH = baseH * Math.max(1, zoom);
  const fx = Math.min(1, Math.max(0, focalX));
  const fy = Math.min(1, Math.max(0, focalY));
  const offsetX = Math.min(0, Math.max(destW - drawW, destW * fx - drawW * fx));
  const offsetY = Math.min(0, Math.max(destH - drawH, destH * fy - drawH * fy));
  return { drawW, drawH, offsetX, offsetY };
}

// Per-photo tuning for how the 3D model blends into its photo when the two are pixel-composited (see
// drawModelBlended below) -- shared by /customize's real preview and the admin calibration tool's own
// blend preview, same as every other *Config type here.
//
// This replaced an earlier "feather + ambient tint" approximation (which softened the whole object and
// still read as pasted-on) with the VFX-compositing pipeline validated stage-by-stage in
// /dev/composite-poc (see docs/research/glb-photo-composite-blending.md, "Approach 3"):
//   - light wrap: bleed a blurred copy of the BACKGROUND onto a band just inside the silhouette
//     (per-photo: radius + intensity). Keeps the object's own edge sharp.
//   - model softness: a small Gaussian over the model region (per-photo: px). A real photo is never
//     pixel-sharp (lens MTF + sensor optical-low-pass + demosaic soften it ~0.5-1.5px); a render is,
//     so its silhouette AND internal lines (seams, knurling, bolt outlines) read as too crisp.
//   - grain: deterministic monochromatic noise over the silhouette, matched to the plate's sensor grain
//     (per-photo: amount; estimatePhotoGrainSigma derives a starting value from the photo).
//   - linear-light compositing: the model-over-photo edge blend is done in linear light, not gamma --
//     always on, a pure correctness fix, so it isn't a knob here.
// Render supersampling (the POC's other stage) is a <Canvas dpr> setting, applied by the consumers, not
// part of this per-photo config. Pipeline order: linear composite -> light wrap -> model softness -> grain.
export type BlendConfig = {
  lightWrapRadiusPx: number;
  lightWrapIntensity: number;
  modelSoftnessPx: number;
  grainAmount: number;
};
// Defaults = the values hand-tuned against IMG_5148 in the POC. The DB DEFAULT constraints, the C#
// CalibrationImage property initializers, and the SaveCalibrationImageBody record defaults all mirror
// these (database/Tables/CalibrationImages.sql etc.) -- this is the single source of truth.
export const DEFAULT_BLEND_CONFIG: BlendConfig = {
  lightWrapRadiusPx: 11, lightWrapIntensity: 0.15, modelSoftnessPx: 0.5, grainAmount: 9.5,
};

// The <Canvas dpr> multiplier the calibration tool renders the model at, so the silhouette's
// anti-aliasing is sub-pixel (real supersampling past the GPU's own MSAA). The customer /customize
// gallery achieves the same via its own HIDDEN_GL_RENDER_PX fixed-resolution trick and doesn't use this.
export const PREVIEW_RENDER_SCALE = 2;

// --- sRGB <-> linear (LUTs) -------------------------------------------------------------------------
// Canvas 2D composites/blends in sRGB (gamma) space, which lands anti-aliased edges slightly too dark;
// these let the pipeline do the maths in linear light instead.
const SRGB_TO_LIN = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    t[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  return t;
})();
const LIN_TO_SRGB = (() => {
  const t = new Float32Array(1025); // indexed by round(linear * 1024)
  for (let i = 0; i <= 1024; i++) {
    const c = i / 1024;
    t[i] = 255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
  }
  return t;
})();
function linToSrgb255(lin: number): number {
  return LIN_TO_SRGB[lin <= 0 ? 0 : lin >= 1 ? 1024 : Math.round(lin * 1024)];
}

// --- deterministic per-pixel noise (grain) --------------------------------------------------------
// Hashed, not Math.random -- so re-running the composite (which happens on a 200ms poll and on every
// colour change) produces the identical grain pattern rather than shimmering.
function hashUnit(x: number, y: number, seed: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 1274126177)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}
function gaussianAt(x: number, y: number, seed: number): number {
  const u1 = Math.max(1e-7, hashUnit(x, y, seed));
  const u2 = hashUnit(x, y, (seed ^ 0x9e3779b9) >>> 0);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2); // ~N(0,1) via Box-Muller
}
const GRAIN_SEED = 1; // any fixed value works; the pattern just needs to be stable, not persisted

function scratchCanvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  return c;
}

// Everything the pipeline stages need: the background alone (no model), the raw WebGL render (its alpha
// is the model's coverage), the working size, and devicePixelRatio (light-wrap radius is authored in
// CSS px, the canvases are device-pixel-scaled).
// modelX/Y/W/H is glCanvas's own draw transform within this (width x height) buffer -- NOT necessarily
// (0,0,width,height) itself. The two coincide exactly when the destination box already matches glCanvas's
// own aspect ratio (the common case: every full-photo preview, nothing cropped), which is why this used to
// be hardcoded as (0,0,width,height) everywhere and nobody noticed -- but a cover-fit CROPPED destination
// (the /customize collage tiles, square, while glCanvas itself renders at the photo's own real aspect) needs
// its own offset/size here, or the model silhouette used to build every mask in this file (the alpha
// channel every stage below reads) ends up stretched to the box's aspect instead of the model's real shape.
// See drawModelBlended's own comment for where these come from.
type StageContext = {
  bgCanvas: HTMLCanvasElement; glCanvas: HTMLCanvasElement; width: number; height: number; dpr: number;
  modelX: number; modelY: number; modelW: number; modelH: number;
};

// Re-composites the model over the background in LINEAR light. Fully-opaque and fully-transparent pixels
// are copied byte-exact, so only the ~1px partial-alpha AA rim changes -- and (sRGB encode being concave)
// it can only ever get brighter there, never darker.
function compositeLinear(resultCtx: CanvasRenderingContext2D, sc: StageContext) {
  const { bgCanvas, glCanvas, width, height, modelX, modelY, modelW, modelH } = sc;
  const bctx = bgCanvas.getContext("2d");
  const mCanvas = scratchCanvas(width, height);
  const mctx = mCanvas.getContext("2d");
  if (!bctx || !mctx) return;
  mctx.drawImage(glCanvas, modelX, modelY, modelW, modelH);
  const bg = bctx.getImageData(0, 0, width, height).data;
  const m = mctx.getImageData(0, 0, width, height).data;
  const out = resultCtx.createImageData(width, height);
  const o = out.data;
  for (let i = 0; i < o.length; i += 4) {
    const a = m[i + 3];
    o[i + 3] = 255;
    if (a === 0) { o[i] = bg[i]; o[i + 1] = bg[i + 1]; o[i + 2] = bg[i + 2]; continue; }
    if (a === 255) { o[i] = m[i]; o[i + 1] = m[i + 1]; o[i + 2] = m[i + 2]; continue; }
    const af = a / 255;
    for (let c = 0; c < 3; c++) {
      o[i + c] = Math.round(linToSrgb255(SRGB_TO_LIN[bg[i + c]] * (1 - af) + SRGB_TO_LIN[m[i + c]] * af));
    }
  }
  resultCtx.putImageData(out, 0, 0);
}

// The light-wrap band: max(0, A - blur(A, r)) on the model's coverage alpha -- a rim strongest at the
// silhouette, fading to 0 a few px inward, exactly 0 outside. On a model feature thinner than ~2r the
// band fills that feature completely (the blur never recovers to full opacity) -- a radius-too-big
// symptom, not a bug.
function computeLightWrapBand(sc: StageContext, radiusPx: number): Float32Array {
  const { glCanvas, width, height, dpr, modelX, modelY, modelW, modelH } = sc;
  const r = radiusPx * dpr;
  const alpha = scratchCanvas(width, height);
  const ac = alpha.getContext("2d");
  const alphaBlur = scratchCanvas(width, height);
  const abc = alphaBlur.getContext("2d");
  const band = new Float32Array(width * height);
  if (!ac || !abc) return band;
  ac.drawImage(glCanvas, modelX, modelY, modelW, modelH);
  abc.filter = `blur(${r}px)`;
  abc.drawImage(alpha, 0, 0);
  abc.filter = "none";
  const A = ac.getImageData(0, 0, width, height).data;
  const Ab = abc.getImageData(0, 0, width, height).data;
  for (let p = 0, i = 3; p < band.length; p++, i += 4) {
    band[p] = Math.max(0, (A[i] - Ab[i]) / 255);
  }
  return band;
}

// Stage 1: screen a blurred copy of the BACKGROUND onto the light-wrap band (in linear light, to stay
// consistent with compositeLinear). Does NOT soften the object -- only the rim picks up background colour.
function applyLightWrap(resultCtx: CanvasRenderingContext2D, sc: StageContext, radiusPx: number, intensity: number) {
  const { bgCanvas, width, height, dpr } = sc;
  const bgBlur = scratchCanvas(width, height);
  const bb = bgBlur.getContext("2d");
  if (!bb) return;
  bb.filter = `blur(${radiusPx * dpr}px)`;
  bb.drawImage(bgCanvas, 0, 0);
  bb.filter = "none";
  const band = computeLightWrapBand(sc, radiusPx);
  const BB = bb.getImageData(0, 0, width, height).data;
  const out = resultCtx.getImageData(0, 0, width, height);
  const d = out.data;
  for (let p = 0, i = 0; p < band.length; p++, i += 4) {
    if (band[p] === 0) continue;
    const w = Math.min(1, band[p] * intensity);
    for (let c = 0; c < 3; c++) {
      const bl = SRGB_TO_LIN[d[i + c]];
      const wl = SRGB_TO_LIN[BB[i + c]] * w;
      d[i + c] = Math.round(linToSrgb255(1 - (1 - bl) * (1 - wl))); // screen, in linear
    }
  }
  resultCtx.putImageData(out, 0, 0);
}

// Stage 2: add monochromatic (same delta on R/G/B) noise over the silhouette only -- the photo already
// carries grain everywhere else. Applied last, over everything.
function applyGrain(resultCtx: CanvasRenderingContext2D, sc: StageContext, amount: number) {
  const { glCanvas, width, height, modelX, modelY, modelW, modelH } = sc;
  const a = scratchCanvas(width, height);
  const ac = a.getContext("2d");
  if (!ac) return;
  ac.drawImage(glCanvas, modelX, modelY, modelW, modelH);
  const A = ac.getImageData(0, 0, width, height).data;
  const out = resultCtx.getImageData(0, 0, width, height);
  const d = out.data;
  for (let y = 0, i = 0; y < height; y++) {
    for (let x = 0; x < width; x++, i += 4) {
      if (A[i + 3] === 0) continue; // silhouette scope: skip pure background
      const n = gaussianAt(x, y, GRAIN_SEED) * amount;
      d[i] = Math.max(0, Math.min(255, Math.round(d[i] + n)));
      d[i + 1] = Math.max(0, Math.min(255, Math.round(d[i + 1] + n)));
      d[i + 2] = Math.max(0, Math.min(255, Math.round(d[i + 2] + n)));
    }
  }
  resultCtx.putImageData(out, 0, 0);
}

// Estimates the reference photo's own sensor-grain sigma AS IT APPEARS at the composite's working
// resolution (measure the native file and downscaling would make you over-estimate). Tiles into small
// patches, drops clipped or detail-dominated ones, returns a low percentile of the rest -- the flattest
// patches' std is the grain floor. Used by the calibration tool's "Measure from photo" button. Returns
// null if the photo isn't usable yet.
export function estimatePhotoGrainSigma(photo: HTMLImageElement, targetW: number, targetH: number): number | null {
  if (!photo.complete || photo.naturalWidth === 0 || targetW < 32 || targetH < 32) return null;
  const c = scratchCanvas(targetW, targetH);
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(photo, 0, 0, targetW, targetH);
  const data = ctx.getImageData(0, 0, targetW, targetH).data;
  const P = 16;
  const stds: number[] = [];
  for (let py = 0; py + P <= targetH; py += P) {
    for (let px = 0; px + P <= targetW; px += P) {
      let n = 0, sum = 0, sumSq = 0;
      for (let yy = py; yy < py + P; yy++) {
        for (let xx = px; xx < px + P; xx++) {
          const i = (yy * targetW + xx) * 4;
          const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          n++; sum += lum; sumSq += lum * lum;
        }
      }
      const mean = sum / n;
      if (mean < 16 || mean > 239) continue; // clipped -- grain is suppressed
      stds.push(Math.sqrt(Math.max(0, sumSq / n - mean * mean)));
    }
  }
  if (stds.length < 8) return null;
  stds.sort((a, b) => a - b);
  return stds[Math.floor(stds.length * 0.15)];
}

// A small Gaussian over the model region to match the plate's optical softness (lens + sensor
// optical-low-pass + demosaic) -- softens the silhouette AND the model's internal straight lines
// (seams, knurling, bolt outlines), which are shading discontinuities in opaque pixels that nothing
// else in the pipeline touches. Mask = the model's coverage alpha, so it feathers naturally at the rim
// and never touches the photo. Runs after light wrap, before grain (grain must stay per-pixel crisp).
function applyModelSoftness(resultCtx: CanvasRenderingContext2D, sc: StageContext, radiusPx: number) {
  const { glCanvas, width, height, dpr, modelX, modelY, modelW, modelH } = sc;
  const blurred = scratchCanvas(width, height);
  const bc = blurred.getContext("2d");
  if (!bc) return;
  bc.filter = `blur(${radiusPx * dpr}px)`;
  bc.drawImage(resultCtx.canvas, 0, 0);
  bc.filter = "none";

  const alpha = scratchCanvas(width, height);
  const ac = alpha.getContext("2d");
  if (!ac) return;
  ac.drawImage(glCanvas, modelX, modelY, modelW, modelH);
  const A = ac.getImageData(0, 0, width, height).data;
  const B = bc.getImageData(0, 0, width, height).data;
  const out = resultCtx.getImageData(0, 0, width, height);
  const d = out.data;
  for (let i = 0; i < d.length; i += 4) {
    const m = A[i + 3] / 255;
    if (m === 0) continue;
    for (let c = 0; c < 3; c++) d[i + c] = Math.round(d[i + c] * (1 - m) + B[i + c] * m);
  }
  resultCtx.putImageData(out, 0, 0);
}

// Runs the pipeline into `resultCtx`: linear-light composite (always on), then light wrap, then model
// softness, then grain. Each per-photo knob is skipped when it's zeroed.
function runModelPipeline(resultCtx: CanvasRenderingContext2D, sc: StageContext, blend: BlendConfig) {
  resultCtx.clearRect(0, 0, sc.width, sc.height);
  compositeLinear(resultCtx, sc);
  if (blend.lightWrapIntensity > 0 && blend.lightWrapRadiusPx > 0) {
    applyLightWrap(resultCtx, sc, blend.lightWrapRadiusPx, blend.lightWrapIntensity);
  }
  if (blend.modelSoftnessPx > 0) applyModelSoftness(resultCtx, sc, blend.modelSoftnessPx);
  if (blend.grainAmount > 0) applyGrain(resultCtx, sc, blend.grainAmount);
}

// Hard ceiling on the offscreen buffers this allocates -- no real on-screen box (even a big lightbox on
// a high-DPI display) legitimately needs more. Purely a backstop against a caller passing a runaway w/h
// from a sizing bug upstream: confirmed directly, a caller-side feedback loop once fed this a buffer
// that grew every ~200ms until getImageData threw "Out of memory" for real.
const MAX_BLEND_CANVAS_PX = 4000;

// Draws the model layer onto `ctx` composited into whatever's already there (the photo, expected to be
// drawn there at the same x/y/w/h -- every caller does `ctx.drawImage(photo, ...)` first). Snapshots that
// photo region back out as the pipeline's background input, so the model's edge can pick up real
// background colour (light wrap) and blend in linear light. Works at the FINAL on-screen draw box size
// (not glCanvas's own much larger native resolution) so the light-wrap radius means "a few real px"
// consistently -- small collage tile, big lightbox, or the admin blend preview.
//
// `modelCrop` is glCanvas's OWN draw transform, if it differs from the plain x/y/w/h box above -- omit it
// when the photo was drawn uncropped (x/y/w/h IS glCanvas's own aspect already: every full-photo preview --
// the admin main preview, the lightbox, the dev proof-of-concept). Pass it when the caller cover-fit CROPS
// the photo into a box with a different aspect than glCanvas's own (the /customize collage tiles, square,
// while glCanvas renders at the photo's real aspect) -- the same offsetX/offsetY/drawW/drawH the photo
// itself was just drawn with. Without this, every stage below drew glCanvas stretched to fill x/y/w/h
// directly, which is harmless when it already has the right aspect but visibly distorts the model's own
// silhouette (round parts read oval) whenever it doesn't -- confirmed directly against a real cropped
// collage tile, not just reasoned about: a plain, non-stretching crop of the same photo at the same focal
// point/zoom matched the *photo* layer exactly, but the model clearly didn't line up with its own outline.
export function drawModelBlended(
  ctx: CanvasRenderingContext2D, glCanvas: HTMLCanvasElement, x: number, y: number, w: number, h: number, blend: BlendConfig,
  modelCrop?: { x: number; y: number; w: number; h: number },
) {
  const drawW = Math.max(1, Math.min(MAX_BLEND_CANVAS_PX, Math.round(w)));
  const drawH = Math.max(1, Math.min(MAX_BLEND_CANVAS_PX, Math.round(h)));

  const bgCanvas = scratchCanvas(drawW, drawH);
  const bgCtx = bgCanvas.getContext("2d");
  const result = scratchCanvas(drawW, drawH);
  const resultCtx = result.getContext("2d");
  if (!bgCtx || !resultCtx) {
    ctx.drawImage(glCanvas, x, y, w, h); // defensive fallback -- plain composite beats nothing
    return;
  }

  try {
    // Copy the photo region the caller already drew into `ctx` (canvas-space x..x+w) into bgCanvas
    // (0..drawW). Any part outside the source canvas comes through transparent -- fine, the model there
    // is off-box too, so its light-wrap band is ~0.
    bgCtx.drawImage(ctx.canvas, x, y, w, h, 0, 0, drawW, drawH);
    // Rescale modelCrop from x/y/w/h's coordinate space into bgCanvas's own (drawW x drawH) space -- a
    // no-op in practice (MAX_BLEND_CANVAS_PX is far bigger than any real on-screen box), kept general
    // rather than assuming that ceiling can never bite.
    const scaleX = drawW / w, scaleY = drawH / h;
    const crop = modelCrop ?? { x: 0, y: 0, w, h };
    const sc: StageContext = {
      bgCanvas, glCanvas, width: drawW, height: drawH, dpr: (typeof window !== "undefined" && window.devicePixelRatio) || 1,
      modelX: crop.x * scaleX, modelY: crop.y * scaleY, modelW: crop.w * scaleX, modelH: crop.h * scaleY,
    };
    runModelPipeline(resultCtx, sc, blend);
    ctx.drawImage(result, x, y, w, h);
  } catch {
    // getImageData/drawImage can throw if `ctx`'s canvas is ever tainted (not expected for same-origin
    // photos) -- a plain composite is still far better than aborting and drawing nothing.
    ctx.drawImage(glCanvas, x, y, w, h);
  }
}

useGLTF.preload(GLB_PATH);
